import type { ECharts } from 'echarts';

const FALLBACK_BACKGROUND_COLOR = '#14151a';
const CHARTS_GAP_PX = 8;

function resolveBackgroundColor(chart: ECharts): string {
  const backgroundColor = getComputedStyle(chart.getDom()).backgroundColor;

  if (!backgroundColor || backgroundColor === 'rgba(0, 0, 0, 0)' || backgroundColor === 'transparent') {
    return FALLBACK_BACKGROUND_COLOR;
  }

  return backgroundColor;
}

function isWebGLCanvas(canvas: HTMLCanvasElement): boolean {
  try {
    return Boolean(
      canvas.getContext('webgl') || canvas.getContext('webgl2') || canvas.getContext('experimental-webgl'),
    );
  } catch {
    return false;
  }
}

/**
 * ECharts-GL renders the 3D scene on a separate WebGL canvas.
 * `chart.getDataURL()` only captures the 2D zrender layer, so for GL charts we
 * composite every canvas inside the chart DOM (LayerGL uses preserveDrawingBuffer).
 */
function captureChartDataUrl(chart: ECharts): string {
  const canvases = Array.from(chart.getDom().querySelectorAll('canvas')).filter(
    (canvas) => canvas.width > 0 && canvas.height > 0,
  );
  const needsCanvasComposite = canvases.length > 1 || canvases.some(isWebGLCanvas);

  if (!needsCanvasComposite) {
    return chart.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: resolveBackgroundColor(chart),
    });
  }

  const width = Math.max(...canvases.map((canvas) => canvas.width));
  const height = Math.max(...canvases.map((canvas) => canvas.height));
  const exportCanvas = document.createElement('canvas');
  exportCanvas.width = width;
  exportCanvas.height = height;

  const context = exportCanvas.getContext('2d');

  if (!context) {
    return chart.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: resolveBackgroundColor(chart),
    });
  }

  context.fillStyle = resolveBackgroundColor(chart);
  context.fillRect(0, 0, width, height);

  for (const canvas of canvases) {
    context.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, width, height);
  }

  return exportCanvas.toDataURL('image/png');
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load chart screenshot'));
    image.src = dataUrl;
  });
}

function triggerDownload(dataUrl: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = `${fileName}.png`;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

async function captureChartsRowDataUrl(charts: ECharts[]): Promise<string> {
  const images = await Promise.all(charts.map((chart) => loadImage(captureChartDataUrl(chart))));
  const gap = CHARTS_GAP_PX * 2;
  const width = images.reduce((sum, image) => sum + image.width, 0) + gap * (images.length - 1);
  const height = Math.max(...images.map((image) => image.height));
  const exportCanvas = document.createElement('canvas');
  exportCanvas.width = width;
  exportCanvas.height = height;

  const context = exportCanvas.getContext('2d');

  if (!context) {
    return captureChartDataUrl(charts[0]);
  }

  context.fillStyle = resolveBackgroundColor(charts[0]);
  context.fillRect(0, 0, width, height);

  let offsetX = 0;

  for (const image of images) {
    const offsetY = Math.round((height - image.height) / 2);
    context.drawImage(image, offsetX, offsetY);
    offsetX += image.width + gap;
  }

  return exportCanvas.toDataURL('image/png');
}

export function downloadChartScreenshot(charts: ECharts | ECharts[], fileName: string): void {
  const chartList = (Array.isArray(charts) ? charts : [charts]).filter(Boolean);

  if (!chartList.length) {
    return;
  }

  if (chartList.length === 1) {
    triggerDownload(captureChartDataUrl(chartList[0]), fileName);
    return;
  }

  void captureChartsRowDataUrl(chartList)
    .then((dataUrl) => {
      triggerDownload(dataUrl, fileName);
    })
    .catch(() => {
      triggerDownload(captureChartDataUrl(chartList[0]), fileName);
    });
}
